<template>
  <div class="w-full h-full">
    <Card>
      <CardHeader>
        <CardTitle>Perks inventory</CardTitle>
        <CardDescription>Explore your perks inventory</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headersTable1"
          :items="inventory"
          :loading="loading"
          :error="error"
          @retry="getInventory(serverOptions.page - 1, serverOptions.rowsPerPage)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          searchable
          search-placeholder="Search by perk ID or name"
          :search="productSearchInput"
          @update:search="productSearchInput = $event"
          @search="getInventory(0, serverOptions.rowsPerPage)"
          @clear-search="onClearSearch()"
          :filter-groups="inventoryFilterGroups"
          :filters="inventoryFilters"
          @filter-change="onFilterChange"
        >
          <template #item-productId="product">
            <CopyId v-if="product.productId" :value="product.productId" :max-width="140" />
            <span v-else class="text-muted-foreground">—</span>
          </template>

          <template #item-type="product">
            <div class="flex flex-wrap gap-1 items-center">
              <Badge variant="secondary">{{ formatType(product.type) }}</Badge>
              <Badge v-if="product.category" variant="secondary">{{ product.category }}</Badge>
              <Badge v-if="product.country" variant="outline">{{ product.country }}</Badge>
            </div>
          </template>

          <template #item-action="retailer">
            <div class="flex justify-end items-center gap-1">
              <Button variant="outline" size="sm" @click="$router.push(`/product-catalogue/${retailer.productId}`)">View perk</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Badge, CopyId } from '@boogi/ui'
import { DataTable } from '@boogi/ui'

export default {
  name: 'product-inventory',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CopyId,
    Button,
    Badge,
  },
  props: {},

  data() {
    return {
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,
      loading: true,
      error: false,
      productSearchInput: '',
      headersTable1: [
        { text: 'Perk', value: 'productName' },
        { text: 'Perk ID', value: 'productId' },
        { text: 'Retailer', value: 'retailerName' },
        { text: 'Details', value: 'type' },
        { text: 'Purchased', value: 'totalPurchased' },
        { text: 'Remaining', value: 'remaining' },
        { text: 'Assigned', value: 'redeemed' },
        { text: 'Used', value: 'unlocked' },
        { text: 'Action', value: 'action' },
      ],
      productTypes: [
        { id: 'VOUCHER', text: 'Voucher' },
        { id: 'GIFTCARD', text: 'Giftcard' },
      ],
      categories: [],
      countryOptions: [],
      selectedType: [],
      selectedCategory: [],
      selectedCountry: [],
      inventory: [],
    }
  },
  created() {
    this.getCategories()
    this.getCountries()
    this.getInventory(0, 25)
  },
  methods: {
    formatType(type) {
      if (type === 'VOUCHER') return 'Voucher'
      if (type === 'GIFTCARD') return 'Giftcard'
      return type
    },

    onClearSearch() {
      this.productSearchInput = ''
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getInventory(0, this.serverOptions.rowsPerPage)
    },

    onFilterChange(updated) {
      const typeIds = (updated && updated.productType) || []
      this.selectedType = this.productTypes.filter((o) => typeIds.includes(o.id))
      this.selectedCategory = (updated && updated.productCategory) || []
      this.selectedCountry = (updated && updated.country) || []
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getInventory(0, this.serverOptions.rowsPerPage)
    },

    async getCategories() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-catalogue/categories`,
          { method: 'GET', headers: { 'Content-Type': 'application/json', Authorization: `${token}` } }
        )
        if (response.ok) this.categories = await response.json()
      } catch (error) {
        return
      }
    },

    async getCountries() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-catalogue/countries`,
          { method: 'GET', headers: { 'Content-Type': 'application/json', Authorization: `${token}` } }
        )
        if (response.ok) this.countryOptions = await response.json()
      } catch (error) {
        return
      }
    },

    async getInventory(page, size) {
      this.loading = true
      this.error = false
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const params = new URLSearchParams()
        params.append('page', page)
        params.append('size', size)
        if (this.productSearchInput) params.append('search', this.productSearchInput)
        if (this.selectedType.length > 0) {
          params.append('type', this.selectedType.map((t) => t.id).join(','))
        }
        if (this.selectedCategory.length > 0) {
          params.append('category', this.selectedCategory.join(','))
        }
        if (this.selectedCountry.length > 0) {
          params.append('country', this.selectedCountry.join(','))
        }

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-inventory?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.serverItemsLength = object.totalElements
          this.inventory = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    inventoryFilterGroups() {
      const groups = [
        {
          key: 'productType',
          label: 'Type',
          options: this.productTypes.map((o) => ({ value: o.id, label: o.text })),
        },
      ]
      if (this.categories.length > 0) {
        groups.push({
          key: 'productCategory',
          label: 'Category',
          options: this.categories.map((c) => ({ value: c, label: c })),
        })
      }
      if (this.countryOptions.length > 0) {
        groups.push({
          key: 'country',
          label: 'Country',
          options: this.countryOptions.map((c) => ({ value: c, label: c })),
        })
      }
      return groups
    },
    inventoryFilters() {
      return {
        productType: this.selectedType.map((o) => o.id),
        productCategory: this.selectedCategory,
        country: this.selectedCountry,
      }
    },
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getInventory(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
